import { Component, ElementRef, EventEmitter, HostListener, Input, OnChanges, OnInit, Output, SimpleChanges, ViewChild } from '@angular/core';
import { Step } from '../../Models/recorded-action-model';
import { AppType } from '../../Services/mobile-session.service';
import { ApkUploadService } from '../../Services/apk-upload.service';
import { RestService } from '../../Services/rest.service';

// ── Device shape from getDeviceDetails response ───────────────────────────────
export interface MobileDevice {
  _id: string;
  udid: string;
  device_name: string;
  platform: string;
  platform_version: string;
  real_device: boolean;
  status: string;
  sessionStatus: string;
  vm_ip: string;
  vm_name: string | null;
  domain: string;
}

@Component({
  selector: 'ze-apk-upload',
  templateUrl: './apk-upload.component.html',
  styleUrls: ['./apk-upload.component.scss']
})
export class ApkUploadComponent implements OnInit, OnChanges {

  @ViewChild('fileInput', { static: false }) fileInputRef!: ElementRef<HTMLInputElement>;

  @Input()  step: Step = 'upload';
  @Input()  isUploading = false;
  @Input()  uploadProgress = 0;
  @Input()  errorMessage: string | null = null;
  /** Bound two-way via parent — reflects the currently selected platform */
  @Input()  appType: AppType = 'native';

  @Output() uploadComplete  = new EventEmitter<string>();
  @Output() uploadError     = new EventEmitter<string>();
  @Output() errorDismissed  = new EventEmitter<void>();
  @Output() stepChange      = new EventEmitter<Step>();
  /** Emitted when user switches the Native / Flutter toggle */
  @Output() appTypeChanged  = new EventEmitter<AppType>();
  /** Emitted when the user picks a device — parent stores it on the session service */
  @Output() deviceSelected  = new EventEmitter<MobileDevice>();

  @Input() selectedDevice: MobileDevice | null = null;
  selectedFile: File | null = null;

  // ── Device list state ─────────────────────────────────────────────────────────
  devices: MobileDevice[]         = [];
  isLoadingDevices                = false;
  devicesLoadError: string | null = null;
  dropdownOpen                    = false;
  get fileSizeMB(): string {
    return this.selectedFile ? (this.selectedFile.size / 1024 / 1024).toFixed(1) : '0';
  }

  /** True only when a device is online and not already in a session */
  get isDeviceAvailable(): boolean {
    return !!this.selectedDevice
      && this.selectedDevice.status === 'online'
      && this.selectedDevice.sessionStatus === 'available';
  }

  constructor(
    private uploadService: ApkUploadService,
    private restService: RestService
  ) {}

  ngOnInit(): void {
    this.loadDevices();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.selectedDevice && !changes.selectedDevice.currentValue) {
      this.clearFile();
    }
  }

  // ── Platform toggle ───────────────────────────────────────────────────────────

  selectAppType(type: AppType) {
    if (this.appType === type) return;
    this.appType = type;
    this.appTypeChanged.emit(type);
  }

  // ── Device loading ────────────────────────────────────────────────────────────

  loadDevices(): void {
    this.isLoadingDevices  = true;
    this.devicesLoadError  = null;

    this.restService.post('getDeviceDetails', {}).subscribe(
      (response: any) => {
        const list: MobileDevice[] = Array.isArray(response)
          ? response
          : Array.isArray(response.data) ? response.data : [];

        this.devices          = list;
        this.isLoadingDevices = false;
      },
      (_err: any) => {
        this.isLoadingDevices = false;
        this.devicesLoadError = 'Could not load devices. Please try again.';
      }
    );
  }

  selectDevice(device: MobileDevice): void {
    this.selectedDevice = device;
    this.deviceSelected.emit(device);
  }

  toggleDropdown(event: MouseEvent): void {
    event.stopPropagation();
    this.dropdownOpen = !this.dropdownOpen;
  }

  resetDropdown(): void {
    this.deviceSelected.emit(null);
    this.dropdownOpen = false;
  }

  onDropdownSelect(device: MobileDevice): void {
    this.selectDevice(device);
    this.dropdownOpen = false;
  }

  deviceStatusClass(device: MobileDevice): string {
    if (device.status !== 'online')             return 'status--offline';
    if (device.sessionStatus !== 'available')   return 'status--busy';
    return 'status--available';
  }

  deviceStatusLabel(device: MobileDevice): string {
    if (device.status !== 'online')           return 'Offline';
    if (device.sessionStatus !== 'available') return 'In use';
    return 'Available';
  }

  /** "mobileLab3" → "https://mobilelab3.xpeer.ai" */
  private domainToBaseUrl(domain: string): string {
    return `https://${domain.toLowerCase()}.xpeer.ai`;
  }

  // ── File handling ─────────────────────────────────────────────────────────────

  onFileSelected(event: Event) {
    const file = (event.target as HTMLInputElement).files[0];
    if (!file) return;
    if (!this.isValidFile(file)) { this.uploadError.emit('Only .apk files are supported.'); return; }
    this.selectedFile = file;
  }

  onDrop(event: DragEvent) {
    event.preventDefault();
    const file = event.dataTransfer.files[0];
    if (!file) return;
    if (!this.isValidFile(file)) { this.uploadError.emit('Only .apk files are supported.'); return; }
    this.selectedFile = file;
  }

  onDragOver(event: DragEvent) { event.preventDefault(); }

  clearFile() {
    this.selectedFile = null;
    if (this.fileInputRef) this.fileInputRef.nativeElement.value = '';
  }

  dismissError() {
    this.errorDismissed.emit();
    this.stepChange.emit('upload');
  }

  async uploadAndStart() {
    if (!this.selectedFile || !this.selectedDevice) return;
    this.isUploading    = true;
    this.uploadProgress = 0;

    const uploadUrl = `${this.domainToBaseUrl(this.selectedDevice.domain)}/device-farm/api/dashboard/upload`;

    try {
      const formData = new FormData();
      formData.append('file', this.selectedFile, this.selectedFile.name);

      const name = await this.uploadService.uploadWithProgress(
        uploadUrl,
        formData,
        (pct) => { this.uploadProgress = pct; }
      );

      const uploadedName = name || this.selectedFile.name;
      // console.log(`✅ APK uploaded to ${this.selectedDevice.domain} as:`, uploadedName);
      this.uploadComplete.emit(uploadedName);
    } catch (err) {
      this.uploadError.emit((err as any).message || 'Upload failed');
    } finally {
      this.isUploading = false;
    }
  }

  private isValidFile(file: File): boolean {
    return file.name.endsWith('.apk') || file.name.endsWith('.xapk') || file.name.endsWith('.app');;
  }
  @HostListener("document:click")
  onDocumentClick(): void {
    this.dropdownOpen = false;
  }
}